import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
	List,
	ListItem,
	ListItemText,
} from '@mui/material';
import { getQuotation, getQuotationOnlyFlights } from 'features/quotation/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { LoadedQuotation, buildPassengerSlots, getRoomBaseConfigFromQuotation, isFullQuotation } from '../../types';

interface Props {
	onQuotationLoaded: (quotationId: number, quotation: LoadedQuotation) => void;
}

export const StepQuotationEntry = ({ onQuotationLoaded }: Props): JSX.Element => {
	const [quotationIdInput, setQuotationIdInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [preview, setPreview] = useState<LoadedQuotation | null>(null);

	const loadQuotation = async () => {
		const id = Number(quotationIdInput);
		if (!id || id < 1) {
			setError('Ingresá un ID de cotización válido');
			return;
		}

		setLoading(true);
		setError(null);
		setPreview(null);

		try {
			const response = await getQuotation(id);
			setPreview(response.data);
		} catch {
			try {
				const responseOnlyFlights = await getQuotationOnlyFlights(id);
				setPreview(responseOnlyFlights.data);
			} catch (e) {
				setError(extractErrorMessage(e, 'No se encontró la cotización'));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleContinue = () => {
		if (!preview) return;
		onQuotationLoaded(preview.id, preview);
	};

	const slots = preview ? buildPassengerSlots(getRoomBaseConfigFromQuotation(preview)) : [];

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Cotización
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Ingresá el ID de la cotización y presioná Buscar.
			</Typography>

			<Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
				<TextField
					label="Quotation ID"
					type="number"
					size="small"
					value={quotationIdInput}
					onChange={(e) => setQuotationIdInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							void loadQuotation();
						}
					}}
					sx={{ width: 220 }}
					disabled={loading}
				/>
				<Button variant="contained" onClick={() => void loadQuotation()} disabled={loading}>
					{loading ? <CircularProgress size={22} color="inherit" /> : 'Buscar'}
				</Button>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}

			{preview && (
				<Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600}>
						{preview.name} · Caso {preview.crmTicket}
					</Typography>
					{preview.phone && (
						<Typography variant="body2" color="text.secondary">
							Tel: {preview.phone}
						</Typography>
					)}
					<List dense sx={{ mt: 1 }}>
						<ListItem disablePadding>
							<ListItemText
								primary={`Pasajeros a cargar: ${slots.length}`}
								secondary={slots.map((s) => s.label).join(', ')}
							/>
						</ListItem>
						{isFullQuotation(preview) && (
							<>
								<ListItem disablePadding>
									<ListItemText primary={`Opciones de vuelo: ${preview.airs?.length || 0}`} />
								</ListItem>
								<ListItem disablePadding>
									<ListItemText
										primary={`Destinos: ${preview.destination?.length || 0}`}
										secondary={preview.destination?.map((d) => d.cityName).join(' · ')}
									/>
								</ListItem>
							</>
						)}
					</List>
					<Box mt={2} display="flex" justifyContent="flex-end">
						<Button variant="contained" color="success" onClick={handleContinue}>
							Continuar
						</Button>
					</Box>
				</Paper>
			)}
		</Box>
	);
};
